import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { Section, Box, Flex, Text, Card, Badge } from '@radix-ui/themes';
import { HeartIcon, HeartFilledIcon, StarFilledIcon, StarIcon } from '@radix-ui/react-icons';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';

import PageTitle from 'components/layout/PageTitle';
import { userInfo } from 'store/slices/users';
import { fetchIssuesByForm, fetchVoteForms } from 'util/formVote';

export default function VoteResult() {
  const currentUser = useSelector(userInfo);
  const currentUserId = currentUser?.userId;
  const requestSeq = useRef(0);

  const [loading, setLoading] = useState(true);
  const [formList, setFormList] = useState([]);
  const [selectedForm, setSelectedForm] = useState('');
  const [projectList, setProjectList] = useState([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [visibleGroups, setVisibleGroups] = useState([]);

  const fetchFormList = useCallback(async () => {
    setLoading(true);
    try {
      setFormList(await fetchVoteForms());
    } catch (error) {
      console.error('양식 목록 조회 실패:', error);
      setFormList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProjectList = useCallback(
    async formId => {
      if (!formId) return;
      const seq = ++requestSeq.current;
      setProjectLoading(true);
      setProjectList([]);
      setVisibleGroups([]);

      try {
        const issues = await fetchIssuesByForm(formId, currentUserId);
        if (seq !== requestSeq.current) return;
        const sortedProjects = [...issues].sort((a, b) => b.thumbsUpCount - a.thumbsUpCount);
        setProjectList(sortedProjects);
        setVisibleGroups(getRankingGroups(sortedProjects).map((_, index) => index));
      } catch (error) {
        console.error('이슈 목록 조회 실패:', error);
        if (seq !== requestSeq.current) return;
        setProjectList([]);
        setVisibleGroups([]);
      } finally {
        if (seq === requestSeq.current) setProjectLoading(false);
      }
    },
    [currentUserId]
  );

  const handleFormSelect = formId => {
    setSelectedForm(formId);
    fetchProjectList(formId);
  };

  const handleProjectClick = issueId => {
    window.open(`/localIssueFinder/detail/${issueId}`, '_blank');
  };

  // 순위 그룹 계산 함수
  const getRankingGroups = projects => {
    const groups = [];
    let currentRank = 1;
    let currentGroup = [];
    let currentScore = null;

    projects.forEach((project, index) => {
      if (currentScore === null || project.thumbsUpCount === currentScore) {
        // 첫 번째 항목이거나 같은 점수인 경우
        currentGroup.push({ ...project, rank: currentRank });
        currentScore = project.thumbsUpCount;
      } else {
        // 점수가 다른 경우, 이전 그룹을 완성하고 새 그룹 시작
        groups.push(currentGroup);
        currentRank = groups.reduce((acc, group) => acc + group.length, 0) + 1;
        currentGroup = [{ ...project, rank: currentRank }];
        currentScore = project.thumbsUpCount;
      }
    });

    // 마지막 그룹 추가
    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }

    return groups;
  };

  // 순위 아이콘 및 색상 반환 함수
  const getRankingInfo = groupIndex => {
    switch (groupIndex) {
      case 0:
        return {
          icon: <StarFilledIcon size={20} />,
          color: '#FFD700', // 금색
          bgColor: '#FFF9E6',
          borderColor: '#FFD700',
          fontColor: '#000000',
          text: '1위',
          cardScale: 1.5
        };
      case 1:
        return {
          icon: <StarFilledIcon size={20} />,
          color: '#C0C0C0', // 은색
          bgColor: '#F5F5F5',
          borderColor: '#C0C0C0',
          fontColor: '#000000',
          text: '2위',
          cardScale: 1
        };
      case 2:
        return {
          icon: <StarFilledIcon size={20} />,
          color: '#CD7F32', // 동색
          bgColor: '#FDF5E6',
          borderColor: '#CD7F32',
          fontColor: '#000000',
          text: '3위',
          cardScale: 1
        };
      default:
        return {
          icon: <StarIcon size={20} />,
          color: '#666666',
          bgColor: '#F9F9F9',
          borderColor: '#E5E5E5',
          fontColor: '#000000',
          text: `${groupIndex + 1}위`,
          cardScale: 1
        };
    }
  };

  // 순위별 프로젝트 그룹화
  const rankingGroups = useMemo(() => {
    return getRankingGroups(projectList);
  }, [projectList]);

  // 컴포넌트 마운트 시 아젠다 목록 조회
  useEffect(() => {
    fetchFormList();
  }, [fetchFormList]);

  return (
    <Section>
      <PageTitle title="투표 결과" />

      {loading ? (
        <Box p="6" style={{ textAlign: 'center' }}>
          <Text>로딩 중...</Text>
        </Box>
      ) : (
        <Box>
          {/* 아젠다 선택 섹션 */}
          <Box className="box box-white">
            <Text as="p" size="4" weight="bold" mb="var(--space-3)">
              양식을 선택하여주세요.
            </Text>

            {formList.length === 0 ? (
              <Text color="gray">활성화된 양식이 없습니다.</Text>
            ) : (
              <Box
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, 320px)',
                  gap: '16px'
                }}
              >
                {formList.map(form => (
                  <Card
                    key={form.formId}
                    p="4"
                    style={{
                      border: selectedForm === form.formId ? '2px solid var(--accent-9)' : '1px solid var(--gray-6)',
                      backgroundColor: selectedForm === form.formId ? 'var(--accent-2)' : 'var(--color-background)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      height: '120px',
                      minHeight: '120px',
                      maxHeight: '120px'
                    }}
                    onClick={() => handleFormSelect(form.formId)}
                  >
                    <Flex direction="column" justify="between" style={{ height: '100%' }}>
                      <Flex align="flex-start" justify="between" gap="2">
                        <Text
                          size="4"
                          weight="medium"
                          style={{
                            flex: 1,
                            lineHeight: '1.4',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {form.formName}
                        </Text>
                        {selectedForm === form.formId && (
                          <Box
                            style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              backgroundColor: 'var(--accent-9)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <Text size="1" style={{ color: 'white' }}>
                              ✓
                            </Text>
                          </Box>
                        )}
                      </Flex>
                      {form.startDate && form.endDate && (
                        <Text size="2" color="gray">
                          {dayjs(form.startDate).format('YYYY-MM-DD')} ~ {dayjs(form.endDate).format('YYYY-MM-DD')}
                        </Text>
                      )}
                    </Flex>
                  </Card>
                ))}
              </Box>
            )}
          </Box>

          {/* 투표 결과 섹션 */}
          {selectedForm && (
            <Box className="box box-white">
              <Text as="p" size="4" weight="bold" mb="var(--space-3)">
                투표 결과
              </Text>

              {projectLoading ? (
                <Box p="4" style={{ textAlign: 'center' }}>
                  <Text>투표 결과 로딩 중...</Text>
                </Box>
              ) : projectList.length === 0 ? (
                <Text color="gray">해당 양식의 이슈가 없습니다.</Text>
              ) : (
                <Flex direction="column" gap="4">
                  {rankingGroups.map((group, groupIndex) => {
                    const rankInfo = getRankingInfo(groupIndex);
                    const isVisible = visibleGroups.includes(groupIndex);

                    return (
                      <Box
                        key={groupIndex}
                        style={{
                          opacity: isVisible ? 1 : 0,
                          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                          transition: 'all 0.6s ease-out',
                          animation: isVisible ? 'slideInUp 0.6s ease-out' : 'none'
                        }}
                      >
                        {/* 순위 그룹 헤더 */}
                        <Box
                          p="4"
                          mb="3"
                          style={{
                            backgroundColor: rankInfo.bgColor,
                            border: `2px solid ${rankInfo.borderColor}`,
                            borderRadius: '12px'
                          }}
                        >
                          <Flex align="center" gap="3">
                            <Box
                              style={{
                                backgroundColor: rankInfo.color,
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white'
                              }}
                            >
                              {rankInfo.icon}
                            </Box>
                            <Flex direction="column" gap="1">
                              <Text size="5" weight="bold" style={{ color: rankInfo.fontColor }}>
                                {rankInfo.text}
                                {group.length > 1 && ` (공동 ${group.length}개)`}
                              </Text>
                              <Flex align="center" gap="2">
                                <Badge
                                  variant="solid"
                                  style={{
                                    backgroundColor: rankInfo.color,
                                    color: 'white'
                                  }}
                                >
                                  {group[0].thumbsUpCount}표
                                </Badge>
                                <Text size="2" color="gray">
                                  총 {group.length}개 이슈
                                </Text>
                              </Flex>
                            </Flex>
                          </Flex>
                        </Box>

                        {/* 해당 순위의 프로젝트들 */}
                        <Box
                          style={{
                            display: 'grid',
                            gridTemplateColumns: groupIndex <= 1 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                            gap: '16px',
                            marginBottom: '24px'
                          }}
                        >
                          {group.map((project, projectIndex) => (
                            <Card
                              key={project.id || projectIndex}
                              p="3"
                              style={{
                                border: '1px solid #e5e5e5',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                position: 'relative',
                                opacity: isVisible ? 1 : 0,
                                transform: isVisible ? 'scale(1)' : 'scale(0.95)',
                                animationDelay: `${projectIndex * 0.1}s`
                              }}
                              onClick={() => handleProjectClick(project.id)}
                            >
                              <Flex direction="column" gap="3">
                                {/* 썸네일 이미지 */}
                                <Box
                                  style={{
                                    width: '100%',
                                    aspectRatio: '16/9',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    position: 'relative'
                                  }}
                                >
                                  {/* 순위 배지 */}
                                  <Box
                                    style={{
                                      position: 'absolute',
                                      top: '8px',
                                      left: '8px',
                                      zIndex: 1,
                                      backgroundColor: rankInfo.color,
                                      borderRadius: '50%',
                                      width: '32px',
                                      height: '32px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}
                                  >
                                    <Box style={{ color: 'white' }}>{rankInfo.icon}</Box>
                                  </Box>

                                  {project.thumbnail ? (
                                    <img
                                      src={project.thumbnail}
                                      alt={project.title}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                      }}
                                    />
                                  ) : (
                                    <Flex align="center" justify="center" style={{ height: '100%' }}>
                                      <Text size="2" color="gray">
                                        이미지 없음
                                      </Text>
                                    </Flex>
                                  )}
                                </Box>

                                {/* 프로젝트 정보 */}
                                <Flex direction="column" gap="2">
                                  <Text
                                    weight="medium"
                                    style={{
                                      fontSize: '20px',
                                      lineHeight: '1.4',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      height: '2.8em',
                                      minHeight: '2.8em',
                                      maxHeight: '2.8em'
                                    }}
                                  >
                                    {project.title}
                                  </Text>

                                  <Flex align="center" justify="between">
                                    <Flex align="center" gap="1">
                                      {project.thumbsUpCount > 0 ? (
                                        <HeartFilledIcon size={20} color="var(--blue-9)" />
                                      ) : (
                                        <HeartIcon size={20} color="var(--blue-9)" />
                                      )}
                                      <Text color="blue" weight="medium" style={{ fontSize: '16px' }}>
                                        {project.thumbsUpCount}
                                      </Text>
                                    </Flex>
                                    <Badge
                                      variant="outline"
                                      style={{
                                        borderColor: rankInfo.color,
                                        color: rankInfo.fontColor,
                                        fontSize: '16px',
                                        padding: '4px 8px'
                                      }}
                                    >
                                      {project.rank}위
                                    </Badge>
                                  </Flex>
                                </Flex>
                              </Flex>
                            </Card>
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Flex>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* 애니메이션을 위한 CSS */}
      <style>
        {`
          @keyframes slideInUp {
            0% {
              opacity: 0;
              transform: translateY(30px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes fadeInScale {
            0% {
              opacity: 0;
              transform: scale(0.95);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>
    </Section>
  );
}
